import { BreakpointObserver } from '@angular/cdk/layout';
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostListener,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import {
    DOCUMENT_IMAGE_MIME_ACCEPT,
    IDocument,
    NotificationType,
    SafeAny,
    ToastType,
} from '@pf/shared-common';
import { NotificationsService } from '../notifications/services/notifications.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NzBeforeUploadFileType, NzUploadFile } from 'ng-zorro-antd/upload';
import { catchError, EMPTY, finalize, map, of, take } from 'rxjs';
import { DOCUMENT_IMAGE_GALLERY } from './document-image-gallery.tokens';

@UntilDestroy()
@Component({
    selector: 'pf-document-image-gallery',
    templateUrl: './pf-document-image-gallery.component.html',
    styleUrls: ['./pf-document-image-gallery.component.scss'],
    standalone: false,
})
export class PfDocumentImageGalleryComponent implements OnInit, OnDestroy {
    private readonly gallery = inject(DOCUMENT_IMAGE_GALLERY);
    private readonly notificationsService = inject(NotificationsService);
    private readonly breakpointObserver = inject(BreakpointObserver);
    private readonly cdr = inject(ChangeDetectorRef);

    @Input({ required: true }) files!: IDocument[];
    @Input() parentId: string | undefined;

    @Input() maxImages = 5;
    @Input() accept = DOCUMENT_IMAGE_MIME_ACCEPT;

    @Output() filesChanged = new EventEmitter<void>();

    previewCompactViewport = false;

    settingPrimaryFileId: string | null = null;
    imageUploading = false;

    previewVisible = false;
    previewLoading = false;
    previewSrc: string | null = null;
    previewAlt = '';
    previewGallery: IDocument[] = [];
    previewIndex = 0;
    private previewObjectUrl: string | null = null;
    private previewGeneration = 0;

    ngOnInit(): void {
        this.breakpointObserver
            .observe(['(max-width: 1024px)'])
            .pipe(untilDestroyed(this))
            .subscribe(state => {
                this.previewCompactViewport = state.matches;
                this.cdr.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this.closePreview();
    }

    listLabel(doc: IDocument): string {
        const base = doc.fileName?.trim() || 'Image';
        return doc.isDefault ? `★ ${base}` : base;
    }

    private filterNonDeleted(files: IDocument[]): IDocument[] {
        return (files ?? []).filter(d => !d.isDeleted);
    }

    get previewShowGalleryChrome(): boolean {
        return this.previewGallery.length > 1;
    }

    get previewActiveDoc(): IDocument | undefined {
        const g = this.previewGallery;
        const i = this.previewIndex;
        return g.length > 0 && i >= 0 && i < g.length ? g[i] : undefined;
    }

    get previewModalNzStyle(): Record<string, string> {
        if (!this.previewCompactViewport) {
            return {};
        }
        return {
            top: '0',
            paddingBottom: '0',
            margin: '0',
            maxWidth: '100%',
        };
    }

    get previewModalBodyStyle(): Record<string, string> {
        if (!this.previewCompactViewport) {
            return {};
        }
        return {
            height: 'calc(100vh - 55px)',
            maxHeight: 'calc(100vh - 55px)',
            overflow: 'auto',
            padding: '8px 12px',
            paddingBottom: 'max(8px, env(safe-area-inset-bottom, 0px))',
        };
    }

    openPreview(doc: IDocument, allFiles: IDocument[]): void {
        if (!doc.fileId && !doc.url) {
            return;
        }
        const gallery = this.filterNonDeleted(allFiles ?? []).filter(
            d => !!(d.fileId || d.url)
        );
        const idx = gallery.findIndex(d => d.id === doc.id);
        if (idx < 0) {
            return;
        }
        this.previewGallery = gallery;
        this.previewIndex = idx;
        this.previewVisible = true;
        this.revokePreviewObjectUrl();
        this.previewSrc = null;
        this.loadPreviewDocument(doc);
    }

    previewNext(): void {
        const n = this.previewGallery.length;
        if (n <= 1) {
            return;
        }
        const next = (this.previewIndex + 1) % n;
        this.goToGalleryIndex(next);
    }

    previewPrev(): void {
        const n = this.previewGallery.length;
        if (n <= 1) {
            return;
        }
        const prev = (this.previewIndex - 1 + n) % n;
        this.goToGalleryIndex(prev);
    }

    goToGalleryIndex(i: number): void {
        if (
            i === this.previewIndex ||
            i < 0 ||
            i >= this.previewGallery.length
        ) {
            return;
        }
        const doc = this.previewGallery[i];
        this.previewIndex = i;
        this.revokePreviewObjectUrl();
        this.previewSrc = null;
        this.cdr.markForCheck();
        this.loadPreviewDocument(doc);
    }

    @HostListener('document:keydown', ['$event'])
    previewKeydown(event: KeyboardEvent): void {
        if (!this.previewVisible) {
            return;
        }
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.previewPrev();
        } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.previewNext();
        }
    }

    private revokePreviewObjectUrl(): void {
        if (this.previewObjectUrl) {
            URL.revokeObjectURL(this.previewObjectUrl);
            this.previewObjectUrl = null;
        }
    }

    private loadPreviewDocument(doc: IDocument): void {
        const name = doc.fileName?.trim() || 'Image';
        this.previewAlt = doc.isDefault ? `${name} (primary list image)` : name;
        this.previewLoading = true;
        const previewGenerationToken = ++this.previewGeneration;
        this.cdr.markForCheck();

        if (doc.fileId) {
            this.gallery
                .previewUrl$(doc, this.parentId)
                .pipe(
                    take(1),
                    catchError(() => {
                        if (doc.url) {
                            return of(doc.url);
                        }
                        if (previewGenerationToken === this.previewGeneration) {
                            this.notificationsService.create({
                                type: NotificationType.ToastMessage,
                                toastType: ToastType.error,
                                message: 'Could not load image preview.',
                            } as SafeAny);
                            this.previewVisible = false;
                        }
                        return of(null);
                    }),
                    finalize(() => {
                        if (previewGenerationToken === this.previewGeneration) {
                            this.previewLoading = false;
                            this.cdr.markForCheck();
                        }
                    }),
                    untilDestroyed(this)
                )
                .subscribe(url => {
                    if (previewGenerationToken !== this.previewGeneration) {
                        if (url?.startsWith('blob:')) {
                            URL.revokeObjectURL(url);
                        }
                        return;
                    }
                    if (url) {
                        if (url.startsWith('blob:')) {
                            this.previewObjectUrl = url;
                        }
                        this.previewSrc = url;
                    }
                    this.cdr.markForCheck();
                });
        } else if (doc.url) {
            this.previewSrc = doc.url;
            this.previewLoading = false;
            this.cdr.markForCheck();
        } else {
            this.previewLoading = false;
            this.cdr.markForCheck();
        }
    }

    closePreview(): void {
        this.revokePreviewObjectUrl();
        this.previewSrc = null;
        this.previewGallery = [];
        this.previewIndex = 0;
        this.previewVisible = false;
        this.previewLoading = false;
        this.cdr.markForCheck();
    }

    private fileFromNzUpload(file: NzUploadFile): File | null {
        const raw: unknown =
            (file as NzUploadFile & { originFileObj?: unknown })
                .originFileObj ?? file;
        if (!(raw instanceof Blob)) {
            return null;
        }
        if (raw instanceof File) {
            return raw;
        }
        const name = file.name || 'image';
        return new File([raw], name, { type: raw.type || 'image/jpeg' });
    }

    beforeUpload = (
        file: NzUploadFile,
        _fileList: NzUploadFile[]
    ): NzBeforeUploadFileType => {
        const uploadFile = this.fileFromNzUpload(file);
        const pid = this.parentId?.trim();
        if (!uploadFile || !pid) {
            if (!pid) {
                this.notificationsService.create({
                    type: NotificationType.ToastMessage,
                    toastType: ToastType.warning,
                    message: 'Save the item first, then upload images.',
                } as SafeAny);
            } else {
                this.notificationsService.create({
                    type: NotificationType.ToastMessage,
                    toastType: ToastType.warning,
                    message: 'Could not read the selected file.',
                } as SafeAny);
            }
            return false;
        }
        this.imageUploading = true;
        this.cdr.markForCheck();
        this.gallery
            .uploadImage$(pid, uploadFile, { fileName: uploadFile.name })
            .pipe(
                catchError(() => {
                    this.notificationsService.create({
                        type: NotificationType.ToastMessage,
                        toastType: ToastType.error,
                        message: 'Could not upload image.',
                    } as SafeAny);
                    return EMPTY;
                }),
                finalize(() => {
                    this.imageUploading = false;
                    this.cdr.markForCheck();
                }),
                untilDestroyed(this)
            )
            .subscribe({
                next: () => {
                    this.filesChanged.emit();
                },
            });
        return false;
    };

    setPrimary(doc: IDocument): void {
        const pid = this.parentId?.trim();
        if (!pid || !doc.id || doc.isDefault) {
            return;
        }
        this.settingPrimaryFileId = doc.id;
        this.cdr.markForCheck();
        this.gallery
            .setPrimaryImage$(pid, doc.id)
            .pipe(
                finalize(() => {
                    this.settingPrimaryFileId = null;
                    this.cdr.markForCheck();
                }),
                untilDestroyed(this)
            )
            .subscribe({
                next: () => {
                    this.filesChanged.emit();
                    this.notificationsService.create({
                        type: NotificationType.ToastMessage,
                        toastType: ToastType.success,
                        message: 'Primary image updated.',
                    } as SafeAny);
                },
                error: () => {
                    this.notificationsService.create({
                        type: NotificationType.ToastMessage,
                        toastType: ToastType.error,
                        message: 'Could not update primary image.',
                    } as SafeAny);
                },
            });
    }

    removeDoc(doc: IDocument): void {
        const pid = this.parentId?.trim();
        if (!pid || !doc.id) {
            return;
        }
        this.gallery
            .deleteImage$(pid, doc.id)
            .pipe(
                map(() => true),
                catchError(() => of(false)),
                untilDestroyed(this)
            )
            .subscribe(ok => {
                if (ok) {
                    this.filesChanged.emit();
                }
            });
    }
}
